import { useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { IconAlertTriangle } from '@tabler/icons-react';
import {
  DEFAULT_PLAN_DAYS,
  SUBSCRIPTION_PLAN_LABELS,
  formatDaysLeft,
  previewExtension,
  type AdminUserRow,
  type ChangeSubscriptionInput,
  type EntitlementHistoryEntry,
  type PlanEffect,
  type PlanGrant,
  type SubscriptionPlan,
} from '@creativitas/shared';
import { changeSubscription, revokeEntitlement } from './api';
import { OrderStatusBadges } from './AdminOrdersPage';
import { ADMIN_ORDERS_KEY, fetchOrders } from './payments-api';

/**
 * Тариф пользователя и история его прав (docs/PAYMENTS.md, §4.2, §10).
 *
 * Дарья выдаёт и продлевает тариф, ставит срок «до даты», снимает его и
 * отзывает отдельные выдачи с причиной. Срок считается на сервере из всего
 * журнала; здесь — только предварительный расчёт по тем же правилам, чтобы до
 * нажатия было видно, что получится и сколько дней Лайт сгорит при переходе.
 */

const formatDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';

type Action = ChangeSubscriptionInput['action'];

type Callbacks = {
  busy: boolean;
  onChanged: (message: string) => void;
  onError: (error: unknown) => void;
};

/** Короткая подпись тарифа для таблицы пользователей. */
export function PlanSummary({ subscription }: { subscription: AdminUserRow['access']['subscription'] }) {
  if (subscription.active && subscription.plan) {
    return (
      <Text size="sm" c="var(--gold-deep)" fw={600}>
        {SUBSCRIPTION_PLAN_LABELS[subscription.plan]} до {formatDate(subscription.endsAt)}
      </Text>
    );
  }
  if (subscription.expired && subscription.lastPlan) {
    return (
      <Text size="sm" c="var(--text-muted)">
        {SUBSCRIPTION_PLAN_LABELS[subscription.lastPlan]} закончился {formatDate(subscription.endsAt)}
      </Text>
    );
  }
  return (
    <Badge variant="default" size="sm">
      нет
    </Badge>
  );
}

/** Выдачи тарифа, которые участвуют в расчёте, — из истории. */
function planGrantsFrom(history: EntitlementHistoryEntry[]): PlanGrant[] {
  return history
    .filter((entry) => entry.type === 'subscription' && entry.plan !== null && entry.revokedAt === null)
    .map((entry) => ({
      id: entry.id,
      plan: entry.plan!,
      days: entry.days,
      endsAt: entry.endsAt,
      grantedAt: entry.grantedAt,
    }));
}

/** Конец выбранного дня по местному времени — так «до 31.12» значит «весь день 31-го». */
function endOfDay(date: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year!, month! - 1, day!, 23, 59, 59, 999).toISOString();
}

export function SubscriptionSection({ row, busy, onChanged, onError }: { row: AdminUserRow } & Callbacks) {
  const [action, setAction] = useState<Action>('extend');
  const [plan, setPlan] = useState<SubscriptionPlan>('light');
  const [days, setDays] = useState<number>(DEFAULT_PLAN_DAYS);
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  /** Напоминание за 3 дня до окончания; для подарков и тестовых выдач его можно выключить. */
  const [notifyExpiry, setNotifyExpiry] = useState(true);

  const subscription = row.access.subscription;
  const preview =
    action === 'extend' && days > 0
      ? previewExtension(planGrantsFrom(row.history), plan, days, new Date())
      : null;
  const until = action === 'set' ? endOfDay(date) : null;
  const untilInPast = until !== null && new Date(until).getTime() <= Date.now();

  const mutation = useMutation({
    mutationFn: (input: ChangeSubscriptionInput) => changeSubscription(row.user.id, input),
    onSuccess: (_data, input) => {
      setNote('');
      onChanged(
        input.action === 'cancel'
          ? 'Тариф снят'
          : input.action === 'set'
            ? `Тариф ${SUBSCRIPTION_PLAN_LABELS[input.plan]} установлен до ${formatDate(input.endsAt)}`
            : `Тариф ${SUBSCRIPTION_PLAN_LABELS[input.plan]} выдан на ${input.days} дн.`,
      );
    },
    onError,
  });

  const submit = () => {
    const trimmed = note.trim() || null;
    if (action === 'extend') mutation.mutate({ action, plan, days, note: trimmed, notifyExpiry });
    else if (action === 'set' && until) mutation.mutate({ action, plan, endsAt: until, note: trimmed, notifyExpiry });
    else if (action === 'cancel') mutation.mutate({ action, note: trimmed });
  };

  const canSubmit =
    !busy &&
    !mutation.isPending &&
    (action === 'extend'
      ? Boolean(preview?.allowed)
      : action === 'set'
        ? until !== null && !untilInPast
        : subscription.active);

  return (
    <Stack gap="xs">
      <Title order={5}>Тариф</Title>
      <Text size="sm" c="var(--text-muted)">
        {subscription.active && subscription.plan
          ? `Действует ${SUBSCRIPTION_PLAN_LABELS[subscription.plan]} до ${formatDate(subscription.endsAt)} — осталось ${formatDaysLeft(subscription.daysLeft ?? 0)}.`
          : subscription.expired && subscription.lastPlan
            ? `Тариф ${SUBSCRIPTION_PLAN_LABELS[subscription.lastPlan]} закончился ${formatDate(subscription.endsAt)}.`
            : 'Тарифа нет.'}{' '}
        Про включает всё из Лайт. Владение игрой и точечный доступ от тарифа не зависят.
      </Text>

      <Paper withBorder radius="md" p="sm" style={{ borderColor: 'var(--border-subtle)' }}>
        <Stack gap="sm">
          <SegmentedControl
            size="xs"
            fullWidth
            value={action}
            onChange={(value) => setAction(value as Action)}
            aria-label="Что сделать с тарифом"
            data={[
              { value: 'extend', label: 'Продлить' },
              { value: 'set', label: 'До даты' },
              { value: 'cancel', label: 'Снять' },
            ]}
          />

          {action !== 'cancel' ? (
            <SegmentedControl
              size="xs"
              fullWidth
              value={plan}
              onChange={(value) => setPlan(value as SubscriptionPlan)}
              aria-label="Тариф"
              data={[
                { value: 'light', label: 'Лайт' },
                { value: 'pro', label: 'Про' },
              ]}
            />
          ) : null}

          {action === 'extend' ? (
            <NumberInput
              size="xs"
              label="На сколько дней"
              min={1}
              max={3650}
              allowDecimal={false}
              value={days}
              onChange={(value) => setDays(typeof value === 'number' ? value : Number(value) || 0)}
            />
          ) : null}

          {action === 'set' ? (
            <TextInput
              size="xs"
              type="date"
              label="Действует до (включительно)"
              value={date}
              onChange={(event) => setDate(event.currentTarget.value)}
              error={untilInPast ? 'Дата должна быть в будущем' : undefined}
            />
          ) : null}

          {action !== 'cancel' ? (
            <Checkbox
              size="xs"
              label="Напомнить письмом за 3 дня до окончания"
              checked={notifyExpiry}
              onChange={(event) => setNotifyExpiry(event.currentTarget.checked)}
            />
          ) : null}

          <TextInput
            size="xs"
            label="Комментарий (необязательно)"
            placeholder="Например: оплата переводом 14.10"
            maxLength={300}
            value={note}
            onChange={(event) => setNote(event.currentTarget.value)}
          />

          {action === 'extend' && preview ? (
            preview.allowed ? (
              preview.effect === 'upgraded' ? (
                <Alert variant="light" color="orange" icon={<IconAlertTriangle size={16} />} p="xs">
                  <Text size="xs">
                    Будет: Про до {formatDate(preview.endsAt)}. Лайт прекратится сейчас, оставшиеся{' '}
                    {formatDaysLeft(preview.lostDays)} не переносятся.
                  </Text>
                </Alert>
              ) : (
                <Text size="xs" c="var(--text-muted)">
                  Будет: {SUBSCRIPTION_PLAN_LABELS[plan]} до {formatDate(preview.endsAt)}
                  {preview.effect === 'extended' ? ' — продление от текущей даты окончания.' : '.'}
                </Text>
              )
            ) : (
              <Alert variant="light" color="red" icon={<IconAlertTriangle size={16} />} p="xs">
                <Text size="xs">
                  Лайт нельзя выдать при действующем Про. Чтобы сменить тариф, выберите «До даты».
                </Text>
              </Alert>
            )
          ) : null}

          {action === 'set' ? (
            <Text size="xs" c="var(--text-muted)">
              Перекрывает прежний срок. Так можно и понизить Про до Лайт.
            </Text>
          ) : null}

          {action === 'cancel' && !subscription.active ? (
            <Text size="xs" c="var(--text-muted)">
              Действующего тарифа нет — снимать нечего.
            </Text>
          ) : null}

          <Group>
            <Button
              size="xs"
              color={action === 'cancel' ? 'red' : undefined}
              disabled={!canSubmit}
              loading={mutation.isPending}
              onClick={submit}
            >
              {action === 'cancel' ? 'Снять тариф' : 'Применить'}
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}

const EFFECT_LABELS: Record<PlanEffect, string> = {
  started: 'начат',
  extended: 'продлён',
  upgraded: 'Лайт → Про',
  set: 'срок установлен',
  cancelled: 'снят',
  skipped: 'не применён: действовал Про',
};

export function EntitlementHistory({ row, busy, onChanged, onError }: { row: AdminUserRow } & Callbacks) {
  const [revoking, setRevoking] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: ({ entitlementId, text }: { entitlementId: string; text: string }) =>
      revokeEntitlement(row.user.id, entitlementId, { reason: text }),
    onSuccess: () => {
      setRevoking(null);
      setReason('');
      onChanged('Выдача отозвана, срок пересчитан');
    },
    onError,
  });

  return (
    <Stack gap="xs">
      <Title order={5}>История прав</Title>
      {row.history.length === 0 ? (
        <Text size="sm" c="var(--text-subtle)">
          Прав пока не выдавали.
        </Text>
      ) : (
        <Stack gap={6}>
          {row.history.map((entry) => (
            <Paper
              key={entry.id}
              withBorder
              radius="md"
              p="xs"
              style={{ borderColor: 'var(--border-subtle)', opacity: entry.revokedAt ? 0.7 : 1 }}
              data-testid="history-entry"
            >
              <Stack gap={4}>
                <Group gap="xs" wrap="wrap">
                  <Text size="xs" c="var(--text-muted)">
                    {formatDate(entry.grantedAt)}
                  </Text>
                  <Text size="sm" fw={600} td={entry.revokedAt ? 'line-through' : undefined}>
                    {entry.title}
                  </Text>
                  {entry.revokedAt ? (
                    <Badge size="xs" color="red" variant="light">
                      отозвано
                    </Badge>
                  ) : entry.effect ? (
                    <Badge
                      size="xs"
                      variant="light"
                      color={
                        entry.effect === 'skipped'
                          ? 'red'
                          : entry.effect === 'upgraded' || entry.source === 'upgrade'
                            ? 'orange'
                            : 'gray'
                      }
                    >
                      {EFFECT_LABELS[entry.effect]}
                      {(entry.effect === 'upgraded' || entry.effect === 'cancelled') && entry.lostDays > 0
                        ? `, сгорело ${formatDaysLeft(entry.lostDays)}`
                        : ''}
                    </Badge>
                  ) : null}
                  {!entry.revokedAt && entry.resultEndsAt && entry.effect !== 'skipped' ? (
                    <Text size="xs" c="var(--text-muted)">
                      → до {formatDate(entry.resultEndsAt)}
                    </Text>
                  ) : null}
                </Group>

                <Text size="xs" c="var(--text-muted)">
                  {entry.grantedBy ? `Выдал: ${entry.grantedBy}` : 'Выдано автоматически'}
                  {entry.note ? ` · ${entry.note}` : ''}
                  {entry.orderId ? (
                    <>
                      {' · '}
                      <Anchor component={Link} to={`/admin/orders?id=${entry.orderId}`} size="xs">
                        заказ
                      </Anchor>
                    </>
                  ) : null}
                  {entry.type === 'subscription' && entry.plan !== 'none' && !entry.notifyExpiry
                    ? ' · без напоминания'
                    : ''}
                </Text>

                {entry.revokedAt ? (
                  <Text size="xs" c="var(--text-muted)">
                    Отозвано {formatDate(entry.revokedAt)}
                    {entry.revokedBy ? ` · ${entry.revokedBy}` : ''}
                    {entry.revokeReason ? ` · ${entry.revokeReason}` : ''}
                  </Text>
                ) : revoking === entry.id ? (
                  <Group gap="xs" align="flex-end" wrap="nowrap">
                    <TextInput
                      size="xs"
                      style={{ flex: 1 }}
                      label="Причина отзыва"
                      placeholder="Например: возврат денег"
                      maxLength={300}
                      value={reason}
                      onChange={(event) => setReason(event.currentTarget.value)}
                      autoFocus
                    />
                    <Button
                      size="xs"
                      color="red"
                      disabled={busy || reason.trim() === ''}
                      loading={mutation.isPending}
                      onClick={() => mutation.mutate({ entitlementId: entry.id, text: reason.trim() })}
                    >
                      Отозвать
                    </Button>
                    <Button size="xs" variant="subtle" onClick={() => setRevoking(null)}>
                      Отмена
                    </Button>
                  </Group>
                ) : (
                  <Group>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      disabled={busy}
                      onClick={() => {
                        setRevoking(entry.id);
                        setReason('');
                      }}
                    >
                      {entry.source === 'upgrade' ? 'Вернуть Лайт…' : 'Отозвать…'}
                    </Button>
                  </Group>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/** Заказы пользователя — со ссылкой на карточку заказа (docs/PAYMENTS.md, §10). */
export function UserOrders({ userId }: { userId: string }) {
  const query = useQuery({
    queryKey: [...ADMIN_ORDERS_KEY, 'user', userId],
    queryFn: () => fetchOrders({ userId }),
    staleTime: 0,
  });
  const items = query.data?.items ?? [];
  return (
    <Stack gap="xs">
      <Title order={5}>Заказы</Title>
      {query.isLoading ? (
        <Text size="sm" c="var(--text-subtle)">Загружаем…</Text>
      ) : items.length === 0 ? (
        <Text size="sm" c="var(--text-subtle)">Заказов нет.</Text>
      ) : (
        <Stack gap={4}>
          {items.map((order) => (
            <Group key={order.id} gap="xs" wrap="wrap">
              <Anchor component={Link} to={`/admin/orders?id=${order.id}`} size="sm" ff="monospace">
                № {order.externalOrderId ?? '—'}
              </Anchor>
              <Text size="xs" c="var(--text-muted)">{formatDate(order.paidAt)}</Text>
              <Text size="xs">{order.productTitle ?? order.productCode ?? ''}</Text>
              <OrderStatusBadges order={order} />
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
